// Copyright (c) Docugami, Inc. All rights reserved.

import { createEntityAdapter, createSelector, createSlice, EntityId, EntityState, nanoid, PayloadAction, Update } from '@reduxjs/toolkit';
import { Recipe } from '@reduxjs/toolkit/dist/query/core/buildThunks';
import { createApi } from '@reduxjs/toolkit/query/react'
import { createFolderId, UnassignedDocSetId } from 'components/file_management/file_helpers';
import { ReportLabels } from 'components/reports/ReportsContext';
import { Config } from 'config';
import { ReportQueryParams, ReportViewMode } from 'hooks/navigationService';
import produce, { applyPatches, enablePatches, Patch } from 'immer';
import { debounce } from 'lodash';
import { LabelRepeatsChangedMessage } from 'networking/api';
import { OrganizationSignalRConnectionInstance, waitForSignalRConnection } from 'networking/OrganizationSignalRConnection';
import { AddEntryRequest, AddEntryResponse, BaseMutationResponse, BindEntryAction, BindEntryResponse, BindingProviderId, BindEntryRequest, ConfirmDocumentsRequest, DocumentEvaluationStatus, EntryBindings, GetPublishedAbstractStatusRequest, GetReportBindingEvaluationsResponse, GetReportBindingStatsResponse, GetReportDocumentsResponse, GetReportSchemaResponse, LabelReportEntryRequest, LabelReportEntryResponse, PublishReportRequest, RenameReportRequest, ReportDefinition, ReportDocumentState, ReportEntry, ReportEvaluationUpdated, SetReportEntriesOrderRequest, UnpublishReportRequest, UpdateReportSettingsRequest, BulkConfirmBindingsRequest, ClientRequest, AbstractTemplateUploadRequest, FlagEntryBindingRequest, ReportBindingEvaluationPreview, ReportBinding, GetBindingsResponse, ReportViewContext, BaseEntryBindingsRequest, ModifyGroupEntriesRequest, AddGroupRequest, AddGroupResponse, BaseGroupRequest, LabelGroupRequest, ReportEntryGroup, GroupTabularPreviewResponse, GetGroupTabularPreviewRequest, RefreshGroupTabularSchemaRequest, ReportBindingEvaluation, GetReportBindingEvaluationResponse, ReportStreamChangeNotification, BulkClearBindingsRequest, SetEntryBindingsRequest, IndexChangedMessage, FlaggedResponse } from 'networking/reportsApi';
import { NamedResource } from 'networking/WorkPathApi';
import { AppDispatch, AppState, store } from 'store';
import { createDgBaseQuery } from 'store/baseQuery';
import { startAppListening } from 'store/listenerMiddleware';
import { workPathsSlice } from 'store/work_path/workPathSlice';
import { dgDiligenceFlagKey, dgDiligenceFlagValue } from 'utils/DiligenceReportUtils';
import { Event } from 'utils/Event';
import { clamp } from 'utils/MathUtils';

enablePatches();

export type ReportsApiEndpoint = keyof (typeof reportsApiSlice.endpoints);

export interface OperationMetadata {
    folderId?: string;
    entryId?: string;
}

// We use a constant session ID to associate a given operation with the current report session
export const reportSessionId = nanoid(3);
export const createOperationId = () => `${reportSessionId}-${nanoid(3)}`

interface RemoteOperation extends BaseOperation {
    operationId?: string; // The remote operation ID from the service for the request
    version?: number; // The stream version that will reflect the operation in the associated models
    operationMetadata?: OperationMetadata; // Used only by the client to facilitate client only concerns like querying on going operations.
}

type AddRemoteOperationPayload = Omit<RemoteOperation, "createdAt">;
type AddPatchOperationPayload = Omit<PatchOperation, "createdAt" | "cacheKey">;

interface PatchOperation extends BaseOperation {
    endpoint: ReportsApiEndpoint; // The endpoint for which this operation targets
    cacheKey: string; // The cache key for the endpoint + args combination
    patches: Patch[]; // The patches to apply to the endpoint state to reach the new state
    arg?: any; // The argument passed to the endpoint and used to generate the cache key
}

interface BaseOperation {
    id: string; // Local ID of the operation
    createdAt: number; // The time at which the operation was created - used to sort the operations in the correct order
}

const reportEntryAdapter = createEntityAdapter<ReportEntry>({
    selectId: x => x.id
});

export const reportEntryBindingsAdapter = createEntityAdapter<EntryBindings>({
    selectId: x => x.entryId
});

export const reportDocumentsAdapter = createEntityAdapter<ReportDocumentState>({
    selectId: x => x.folderId
});

const reportEntryGroupAdapter = createEntityAdapter<ReportEntryGroup>({
    selectId: x => x.id
});

export const { selectAll: selectAllGroups } = reportEntryGroupAdapter.getSelectors();

export const getBindingEvaluationKey = (spanId?: string) => spanId ?? '';
export const getBindingEvaluationKeyForBinding = (binding: ReportBinding | undefined) => binding?.spanId ?? '';

const bindingEvaluationPreviewAdapter = createEntityAdapter<ReportBindingEvaluationPreview>({
    selectId: x => getBindingEvaluationKey(x.spanId)
});

export interface GetReportSchemaState {
    entries: EntityState<ReportEntry>;
    groups: EntityState<ReportEntryGroup>;
    report: ReportDefinition;
    version: number;
};

export interface GetDocumentsState {
    documents: EntityState<ReportDocumentState>;
    version: number;
}

export interface GetEvaluationsState {
    evaluations: EntityState<ReportBindingEvaluationPreview>;
    version: string;
}

export type GetReviewDocumentsState = GetDocumentsState;
export type GetPublishedDocumentsState = GetDocumentsState;

export const reportSingleBatchSize = 10;

export interface GetBindingsState {
    entryBindings: EntityState<EntryBindings>;
    fileId: string;
    version: string;
}

export interface RemoveEntriesInput {
    reportId: string;
    entryIds: string[];
    operationId?: string;
}

export interface GetEvaluationsDeltaInput {
    reportId: string;
    fileId: string;
    version: string;
}

export interface GetBindingsDeltaInput {
    reportId: string;
    fileId: string;
    version: string;
}

export interface GetBindingsInput {
    reportId: string;
    fileId: string;
}

export interface GetEvaluationsInput {
    reportId: string;
    fileId: string;
}

export interface RemoveAllBindingsInput {
    reportId: string;
    context: ReportViewContext;
    request: BaseEntryBindingsRequest;
}

export interface BindEntryInput {
    reportId: string;
    context: ReportViewContext;
    request: BindEntryRequest;
}

export interface SetEntryBindingsInput {
    reportId: string;
    context: ReportViewContext;
    request: SetEntryBindingsRequest;
}

export interface FlagEntryBindingInput {
    reportId: string;
    request: FlagEntryBindingRequest;
    context: ReportViewContext;
}

export interface GetBindingEvaluationInput {
    reportId: string;
    evaluationId: string;
}

type WithReportId<T> = T & { reportId: string };
type WithFolderId<T> = T & { folderId: string };
type WithEntryId<T> = T & { entryId: string };

export const getReportBindingsQueryCacheTag = (reportId: string, folderId: string) => `${reportId}.${folderId}`;

const reportsBaseQuery = createDgBaseQuery(Config.reportsApiUrl);
export const reportsApiSlice = createApi({
    reducerPath: 'reportsApi',
    tagTypes: ['Schema', 'Bindings', 'ReviewDocuments', 'PublishedDocuments', 'Evaluations', 'ReviewEvaluationsPreview', 'PublishedEvaluationsPreview', 'ReviewBindingStats', 'AbstractPreview', 'Schema', 'GroupTabularOutput', 'Flagged'],
    baseQuery: reportsBaseQuery,
    endpoints: builder => ({
        getReportSchema: builder.query<GetReportSchemaState, string>({
            query: (reportId: string) => `/stream/reports/${encodeURIComponent(reportId)}`,
            providesTags: (result, error, arg) => [{ type: 'Schema', id: arg }],
            transformResponse: (result: GetReportSchemaResponse): GetReportSchemaState => {
                return {
                    entries: reportEntryAdapter.setAll(reportEntryAdapter.getInitialState(), result.entries),
                    groups: reportEntryGroupAdapter.setAll(reportEntryGroupAdapter.getInitialState(), result.groups ?? []),
                    report: result.report,
                    version: result.version
                }
            },
            onQueryStarted: async (arg, api) => {
                const response = await api.queryFulfilled;
                const versionPayload: EndpointVersionPayload = { endpoint: "getReportSchema", arg: arg, version: response.data.version };
                api.dispatch(handleVersionedEndpointResponse(versionPayload, () => api.dispatch(reportsApiSlice.util.invalidateTags([{ type: 'Schema', id: arg }]))));
            }
        }),
        getReviewDocuments: builder.query<GetReviewDocumentsState, string>({
            query: (reportId: string) => `/stream/reports/${encodeURIComponent(reportId)}/documents/review`,
            providesTags: (result, error, arg) => [{ type: 'ReviewDocuments', id: arg }],
            transformResponse: (result: GetReportDocumentsResponse): GetReviewDocumentsState => {
                return {
                    documents: reportDocumentsAdapter.setAll(
                        reportDocumentsAdapter.getInitialState(),
                        result.documents.filter(x => x.evaluationStatus === DocumentEvaluationStatus.None).slice(0, reportSingleBatchSize)),
                    version: result.version
                }
            },
            onQueryStarted: async (arg, api) => {
                const response = await api.queryFulfilled;
                const versionPayload: EndpointVersionPayload = { endpoint: "getReviewDocuments", arg: arg, version: response.data.version };
                api.dispatch(handleVersionedEndpointResponse(versionPayload, () => api.dispatch(reportsApiSlice.util.invalidateTags([{ type: "ReviewDocuments", id: arg }]))));
            }
        }),
        getPublishedDocuments: builder.query<GetPublishedDocumentsState, string>({
            query: (reportId: string) => `/stream/reports/${encodeURIComponent(reportId)}/documents/published`,
            providesTags: (result, error, arg) => [{ type: 'PublishedDocuments', id: arg }],
            transformResponse: (result: GetReportDocumentsResponse): GetPublishedDocumentsState => {
                return {
                    documents: reportDocumentsAdapter.setAll(reportDocumentsAdapter.getInitialState(), result.documents),
                    version: result.version
                }
            },
            onQueryStarted: async (arg, api) => {
                const response = await api.queryFulfilled;
                const versionPayload: EndpointVersionPayload = { endpoint: "getPublishedDocuments", arg: arg, version: response.data.version };
                api.dispatch(handleVersionedEndpointResponse(versionPayload, () => api.dispatch(reportsApiSlice.util.invalidateTags([{ type: "PublishedDocuments", id: arg }]))));
            }
        }),
        getBindings: builder.query<GetBindingsState, GetBindingsInput>({
            query: (arg: GetBindingsInput) => `/stream/reports/${encodeURIComponent(arg.reportId)}/document-bindings/${encodeURIComponent(arg.fileId)}`,
            providesTags: (result, error, arg) => [{ type: 'Bindings', id: arg.fileId }, { type: 'Bindings', id: arg.reportId }],
            transformResponse: (result: GetBindingsResponse, meta, arg): GetBindingsState => {
                return {
                    entryBindings: reportEntryBindingsAdapter.setAll(reportEntryBindingsAdapter.getInitialState(), result.entryBindings),
                    version: result.version,
                    fileId: arg.fileId
                }
            },
            keepUnusedDataFor: 300,
            onQueryStarted: async (arg, api) => {
                const now = new Date().getTime();
                await api.queryFulfilled;
                api.dispatch(invalidateRemoteOpsForVersion({ endpoint: "getBindings", arg: arg, version: now, isTimestamp: true }));
            },
            onCacheEntryAdded: async (arg, api) => {
                await api.cacheDataLoaded;

                const update = debounce(async () => {
                    try {
                        const originalVersion = api.getCacheEntry().data!.version;
                        const now = new Date().getTime();
                        const response = await api.dispatch(reportsApiSlice.endpoints.reportBindingsDelta.initiate({ reportId: arg.reportId, fileId: arg.fileId, version: originalVersion })).unwrap();

                        const beforeState = api.getCacheEntry().data!.entryBindings;
                        api.updateCachedData(x => {
                            x.version = response.version
                            reportEntryBindingsAdapter.upsertMany(x.entryBindings, response.entryBindings.map(y => ({
                                ...y,
                                bindings: y.bindings,
                                suggestions: []
                            })));
                        });

                        api.dispatch(invalidateRemoteOpsForVersion({ endpoint: "getBindings", arg: arg, version: now, isTimestamp: true }));

                        const afterState = api.getCacheEntry().data!.entryBindings;
                        let refreshStats = beforeState.ids.length !== afterState.ids.length;
                        if (!refreshStats) {
                            for (const id of response.entryBindings.map(x => x.entryId)) {
                                const entryBindingsBefore = selectBindingsById(beforeState, id);
                                const entryBindingsAfter = selectBindingsById(afterState, id);
                                const wasConfirmed = entryBindingsBefore?.bindings.every(x => x.userConfirmed);
                                const isConfirmed = entryBindingsAfter?.bindings.every(x => x.userConfirmed);
                                if (wasConfirmed !== isConfirmed) {
                                    refreshStats = true;
                                    break;
                                }
                            }
                        }

                        if (refreshStats) {
                            debounceInvalidateBindingStats(arg.reportId);
                        }
                    }
                    catch (error) {
                        // Simply invalidate the bindings query since we failed to stream in the delta
                        api.dispatch(reportsApiSlice.util.invalidateTags([{ type: "Bindings", id: arg.fileId }]));
                    }
                },
                    1500,
                    {
                        maxWait: 3000,
                        trailing: true,
                        leading: true
                    });


                const handleIndexUpdate = (payload: IndexChangedMessage) => {
                    if (payload.fileId !== arg.fileId) {
                        return;
                    }

                    update();
                }

                const signalRInstance = await waitForSignalRConnection();
                signalRInstance.connection.on("annotationsIndexChanged", handleIndexUpdate);

                try {
                    await api.cacheEntryRemoved;
                }
                finally {
                    OrganizationSignalRConnectionInstance?.connection.off("annotationsIndexChanged", handleIndexUpdate);
                }
            }
        }),
        getEvaluation: builder.query<ReportBindingEvaluation, GetBindingEvaluationInput>({
            query: (request: GetBindingEvaluationInput) => `/stream/reports/${encodeURIComponent(request.reportId)}/evaluations/${encodeURIComponent(request.evaluationId)}`,
            providesTags: (result, error, arg) => [{ type: 'Evaluations', id: arg.evaluationId }],
            transformResponse: (result: GetReportBindingEvaluationResponse): ReportBindingEvaluation => ({
                ...result.evaluation,
                richTextValue: result.evaluation.richTextValue?.replace(/xhtml:/g, '')
            })
        }),
        getEvaluationsPreview: builder.query<GetEvaluationsState, GetEvaluationsInput>({
            query: (arg) => ({
                url: `/stream/reports/${encodeURIComponent(arg.reportId)}/document-evaluations/${encodeURIComponent(arg.fileId)}`
            }),
            providesTags: (result, error, arg) => [{ type: 'ReviewEvaluationsPreview', id: arg.reportId }, { type: 'ReviewEvaluationsPreview', id: arg.fileId }],
            transformResponse: (result: GetReportBindingEvaluationsResponse): GetEvaluationsState => {
                return {
                    evaluations: bindingEvaluationPreviewAdapter.setAll(
                        bindingEvaluationPreviewAdapter.getInitialState(),
                        result.evaluations.map(x => ({ ...x, richTextValue: x.richTextValue?.replace(/xhtml:/g, '') }))),
                    version: result.version,
                }
            },
            keepUnusedDataFor: 300,
            onCacheEntryAdded: async (arg, api) => {
                await api.cacheDataLoaded;

                const update = debounce(async () => {
                    try {
                        const response = await api.dispatch(reportsApiSlice.endpoints.reviewEvaluationsDelta.initiate({ reportId: arg.reportId, fileId: arg.fileId, version: api.getCacheEntry().data!.version })).unwrap();
                        if (!!response.evaluations.length) {
                            api.dispatch(reportsApiSlice.util.invalidateTags(response.evaluations.map(x => ({ type: 'Evaluations', id: x.id }))));
                        }

                        api.updateCachedData(x => {
                            x.version = response.version;
                            if (response.evaluations.length) {
                                bindingEvaluationPreviewAdapter.upsertMany(x.evaluations, response.evaluations);
                            }
                        });
                    }
                    catch (error) {
                        // Simply invalidate the review evaluations query since we failed to stream in the delta
                        api.dispatch(reportsApiSlice.util.invalidateTags([{ type: "ReviewEvaluationsPreview", id: arg.fileId }]));
                    }
                },
                    1500,
                    {
                        maxWait: 3000,
                        trailing: true,
                        leading: true
                    });

                const handleUpdate = (payload: ReportEvaluationUpdated) => {
                    if (arg.reportId !== payload.reportId) {
                        return;
                    }

                    if (!payload.updated.some(x => x.folderId.endsWith(arg.fileId))) {
                        return;
                    }

                    update();
                }

                const handleIndexUpdate = (payload: IndexChangedMessage) => {
                    if (payload.fileId !== arg.fileId) {
                        return;
                    }

                    update();
                }

                const signalRInstance = await waitForSignalRConnection();
                signalRInstance.connection.on("reportEvaluationUpdated", handleUpdate);
                signalRInstance.connection.on("textIndexChanged", handleIndexUpdate);

                try {
                    await api.cacheEntryRemoved;
                }
                finally {
                    OrganizationSignalRConnectionInstance?.connection.off("reportEvaluationUpdated", handleUpdate);
                    OrganizationSignalRConnectionInstance?.connection.off("textIndexChanged", handleIndexUpdate);
                }
            }
        }),
        getReviewBindingStats: builder.query<GetReportBindingStatsResponse, string>({
            query: (reportId: string) => `/stream/reports/${encodeURIComponent(reportId)}/stats/review`,
            providesTags: (result, error, arg) => [{ type: 'ReviewBindingStats', id: arg }]
        }),
        getPublishedAbstractExists: builder.query<boolean, WithReportId<GetPublishedAbstractStatusRequest>>({
            query: input => `/stream/reports/${encodeURIComponent(input.reportId)}/resources/${encodeURIComponent(input.folderId)}/abstract/status`
        }),
        getPublishedSingleDocumentExcelExists: builder.query<boolean, WithReportId<GetPublishedAbstractStatusRequest>>({
            query: input => `/stream/reports/${encodeURIComponent(input.reportId)}/resources/${encodeURIComponent(input.folderId)}/excel/singledocument/status`
        }),
        getPublishedExcelExists: builder.query<boolean, WithReportId<{}>>({
            query: input => `/stream/reports/${encodeURIComponent(input.reportId)}/resources/excel/status`
        }),
        getAbstractTemplateExists: builder.query<boolean, WithReportId<{}>>({
            query: input => `/stream/reports/${encodeURIComponent(input.reportId)}/resources/abstractTemplate/status`
        }),
        getAbstractPreviewExists: builder.query<boolean, WithFolderId<WithReportId<{}>>>({
            query: input => `/stream/reports/${input.reportId}/resources/${input.folderId}/abstract-preview/status`,
            providesTags: ['AbstractPreview']
        }),
        getFlaggedDocuments: builder.query<FlaggedResponse, WithReportId<{}>>({
            query: input => `/stream/reports/${encodeURIComponent(input.reportId)}/flagged-docs`,
            providesTags: () => [{ type: 'Flagged' }]
        }),
        getMiddleOutSuggestions: builder.query<string[], WithFolderId<{}>>({
            query: input => `/files/${encodeURIComponent(input.folderId)}/mo-suggestions`
        }),
        getSuggestedReportStatus: builder.query<boolean, { clusterId: string }>({
            query: input => `workpaths/cluster/${encodeURIComponent(input.clusterId)}/suggestions/status`
        }),
        reviewEvaluationsDelta: builder.mutation<GetReportBindingEvaluationsResponse, GetEvaluationsDeltaInput>({
            query: request => ({
                url: `/stream/reports/${encodeURIComponent(request.reportId)}/document-evaluations/${encodeURIComponent(request.fileId)}`,
                method: 'GET',
                params: {
                    "version": request.version
                }
            })
        }),
        publishedEvaluationsDelta: builder.mutation<GetReportBindingEvaluationsResponse, GetEvaluationsDeltaInput>({
            query: request => ({
                url: `/stream/reports/${encodeURIComponent(request.reportId)}/evaluations`,
                method: 'GET',
                params: {
                    "context": "published",
                    "version": request.version,
                    "preview": true
                }
            })
        }),
        reportBindingsDelta: builder.mutation<GetBindingsResponse, GetBindingsDeltaInput>({
            query: request => ({
                url: `/stream/reports/${encodeURIComponent(request.reportId)}/document-bindings/${encodeURIComponent(request.fileId)}`,
                params: {
                    version: request.version
                },
                method: 'GET'
            })
        }),
        renameReport: builder.mutation<BaseMutationResponse, WithReportId<RenameReportRequest>>({
            query: request => ({
                url: `/stream/reports/${encodeURIComponent(request.reportId)}/rename`,
                method: 'POST',
                body: request
            }),
            onQueryStarted: async (input, api) => {
                const id = api.dispatch(addRemoteOperation(input.operationId));
                api.dispatch(addReportSchemaOptimisticUpdate(input.reportId, x => {
                    x.report.name = input.name;
                }, id));

                try {
                    const response = await api.queryFulfilled;
                    api.dispatch(patchRemoteOpOperationId({ id: id, operationId: response.data.operationId }))
                    api.dispatch(patchRemoteOpVersion({ operationId: response.data.operationId, version: response.data.streamResponse.version }))
                    reportsStreamChanged.raise({
                        reportId: input.reportId,
                        version: response.data.streamResponse.version,
                        changes: response.data.streamResponse.changes
                    });
                }
                catch (error) {
                    api.dispatch(removeRemoteOp(id));
                }
            }
        }),
        setEntriesOrder: builder.mutation<BaseMutationResponse, WithReportId<SetReportEntriesOrderRequest>>({
            query: request => ({
                url: `/stream/reports/${encodeURIComponent(request.reportId)}/entries/order`,
                method: 'POST',
                body: request
            }),
            onQueryStarted: async (input, api) => {
                const id = api.dispatch(addRemoteOperation(input.operationId));
                try {
                    api.dispatch(addReportSchemaOptimisticUpdate(input.reportId, x => {
                        x.report.entries = input.entryIds;
                    }, id));

                    const response = await api.queryFulfilled;
                    api.dispatch(patchRemoteOpOperationId({ id: id, operationId: response.data.operationId }))
                    api.dispatch(patchRemoteOpVersion({ operationId: response.data.operationId, version: response.data.streamResponse.version }))
                    reportsStreamChanged.raise({
                        reportId: input.reportId,
                        version: response.data.streamResponse.version,
                        changes: response.data.streamResponse.changes
                    });
                }
                catch (error) {
                    api.dispatch(removeRemoteOp(id));
                }
            }
        }),
        publishReport: builder.mutation<BaseMutationResponse, WithReportId<PublishReportRequest>>({
            query: request => ({
                url: `/stream/reports/${encodeURIComponent(request.reportId)}/publish`,
                method: 'POST',
                body: request
            }),
            onQueryStarted: async (input, api) => {
                try {
                    const response = await api.queryFulfilled;
                    reportsStreamChanged.raise({
                        reportId: input.reportId,
                        version: response.data.streamResponse.version,
                        changes: response.data.streamResponse.changes
                    });
                }
                catch (error) {
                }
            }
        }),
        uploadAbstractTemplate: builder.mutation<BaseMutationResponse, WithReportId<AbstractTemplateUploadRequest>>({
            query: request => ({
                url: `/stream/reports/${encodeURIComponent(request.reportId)}/resources/abstractTemplate`,
                method: 'POST',
                body: request.formData
            })
        }),
        unpublishReport: builder.mutation<BaseMutationResponse, WithReportId<UnpublishReportRequest>>({
            query: request => ({
                url: `/stream/reports/${encodeURIComponent(request.reportId)}/unpublish`,
                method: 'POST',
                body: request
            }),
            onQueryStarted: async (input, api) => {
                try {
                    const response = await api.queryFulfilled;
                    reportsStreamChanged.raise({
                        reportId: input.reportId,
                        version: response.data.streamResponse.version,
                        changes: response.data.streamResponse.changes
                    });
                }
                catch (error) {
                }
            }
        }),
        updateReportSettings: builder.mutation<BaseMutationResponse, WithReportId<UpdateReportSettingsRequest>>({
            query: request => ({
                url: `/stream/reports/${encodeURIComponent(request.reportId)}/settings`,
                method: 'POST',
                body: request
            }),
            onQueryStarted: async (input, api) => {
                try {
                    const response = await api.queryFulfilled;
                    reportsStreamChanged.raise({
                        reportId: input.reportId,
                        version: response.data.streamResponse.version,
                        changes: response.data.streamResponse.changes
                    });
                }
                catch (error) {
                }
            }
        }),
        confirmDocuments: builder.mutation<BaseMutationResponse, WithReportId<ConfirmDocumentsRequest>>({
            query: request => ({
                url: `/stream/reports/${encodeURIComponent(request.reportId)}/documents/confirm`,
                method: 'POST',
                body: request
            }),
            onQueryStarted: async (input, api) => {
                const recipe = (state: GetDocumentsState): void => {
                    for (const folderId of input.folderIds) {
                        const entity = state.documents?.entities[folderId];
                        if (!!entity) {
                            entity.evaluationStatus = DocumentEvaluationStatus.Confirmed;
                        }
                    }
                };

                const reviewPatches = api.dispatch(reportsApiSlice.util.updateQueryData("getReviewDocuments", input.reportId, recipe))
                const publishPatches = api.dispatch(reportsApiSlice.util.updateQueryData("getPublishedDocuments", input.reportId, recipe))
                try {
                    const response = await api.queryFulfilled;
                    reportsStreamChanged.raise({
                        reportId: input.reportId,
                        version: response.data.streamResponse.version,
                        changes: response.data.streamResponse.changes
                    });
                }
                catch (error) {
                    reviewPatches.undo()
                    publishPatches.undo()
                }
            }
        }),
        bulkConfirmBindings: builder.mutation<BaseMutationResponse, ClientRequest<WithReportId<BulkConfirmBindingsRequest>>>({
            query: operation => ({
                url: `/stream/reports/${encodeURIComponent(operation.request.reportId)}/bindings/confirm`,
                method: 'POST',
                body: operation.request
            }),
            onQueryStarted: async (input, api) => {
                const operationId = input.request.operationId ?? createOperationId()
                const id = api.dispatch(addRemoteOperation(operationId, undefined, input.operationMetadata));
                try {
                    const response = await api.queryFulfilled;
                    debounceInvalidateBindingStats(input.request.reportId);
                    api.dispatch(patchRemoteOpOperationId({ id: id, operationId: response.data.operationId }))
                    api.dispatch(patchRemoteOpVersion({ operationId: response.data.operationId, version: response.data.streamResponse.version }))
                    reportsStreamChanged.raise({
                        reportId: input.request.reportId,
                        version: response.data.streamResponse.version,
                        changes: response.data.streamResponse.changes
                    });
                }
                catch (error) {
                    api.dispatch(removeRemoteOp(id));
                }
            }
        }),
        bulkClearBindings: builder.mutation<BaseMutationResponse, WithReportId<BulkClearBindingsRequest>>({
            query: request => ({
                url: `/stream/reports/${encodeURIComponent(request.reportId)}/bindings`,
                method: 'DELETE',
                body: request
            }),
            onQueryStarted: async (input, api) => {
                const operationId = input.operationId ?? createOperationId()
                const id = api.dispatch(addRemoteOperation(operationId));
                try {
                    const response = await api.queryFulfilled;
                    debounceInvalidateBindingStats(input.reportId);
                    api.dispatch(patchRemoteOpOperationId({ id: id, operationId: response.data.operationId }))
                    api.dispatch(patchRemoteOpVersion({ operationId: response.data.operationId, version: response.data.streamResponse.version }))
                    reportsStreamChanged.raise({
                        reportId: input.reportId,
                        version: response.data.streamResponse.version,
                        changes: response.data.streamResponse.changes
                    });
                }
                catch (error) {
                    api.dispatch(removeRemoteOp(id));
                }
            }
        }),
        bindEntry: builder.mutation<BindEntryResponse, BindEntryInput>({
            query: input => ({
                url: `/stream/reports/${encodeURIComponent(input.reportId)}/entries/bind`,
                method: 'POST',
                body: input.request
            }),
            onQueryStarted: async (input, api) => {
                const request = input.request;
                const operationId = request.operationId ?? createOperationId();
                const id = api.dispatch(addRemoteOperation(operationId));
                try {
                    const initialEntryId: string = request.entryId ?? nanoid(3);
                    const { fileId } = createFolderId(input.request.folderId)
                    api.dispatch(addBindingsOptimisticUpdate(
                        { reportId: input.reportId, fileId: fileId },
                        bindEntryBindingsOptimisticUpdateRecipe(request, initialEntryId),
                        id)
                    );

                    if (!!request.createEntryDefinition) {
                        api.dispatch(addReportSchemaOptimisticUpdate(
                            input.reportId,
                            bindEntryReportSchemaOptimisticUpdateRecipe(request, input.reportId, initialEntryId),
                            id)
                        );
                    }

                    const response = await api.queryFulfilled;
                    debounceInvalidateBindingStats(input.reportId);

                    if (initialEntryId !== response.data.entryId) {
                        // The service has given us the real entry ID so we can update our pending operations
                        api.dispatch(addBindingsOptimisticUpdate(
                            { reportId: input.reportId, fileId: fileId },
                            bindEntryBindingsReplaceEntryIdOptimisticUpdateRecipe(request, initialEntryId, response.data),
                            id
                        ));

                        if (!!request.createEntryDefinition) {
                            api.dispatch(addReportSchemaOptimisticUpdate(
                                input.reportId,
                                bindEntryReportSchemaReplaceEntryIdOptimisticUpdateRecipe(request, initialEntryId, response.data),
                                id
                            ))
                        }
                    }

                    api.dispatch(patchRemoteOpOperationId({ id: id, operationId: response.data.operationId }))
                    api.dispatch(patchRemoteOpVersion({ operationId: response.data.operationId, version: response.data.streamResponse.version }))
                    reportsStreamChanged.raise({
                        reportId: input.reportId,
                        version: response.data.streamResponse.version,
                        changes: response.data.streamResponse.changes
                    });
                }
                catch (error) {
                    api.dispatch(removeRemoteOp(id));
                }
            }
        }),
        setEntryBindings: builder.mutation<BindEntryResponse, SetEntryBindingsInput>({
            query: input => ({
                url: `/stream/reports/${encodeURIComponent(input.reportId)}/entries/${encodeURIComponent(input.request.entryId)}/bindings`,
                method: 'PUT',
                body: input.request
            }),
            onQueryStarted: async (input, api) => {
                const request = input.request;
                const operationId = request.operationId ?? createOperationId();
                const id = api.dispatch(addRemoteOperation(operationId));
                try {
                    const entryId: string = request.entryId;
                    const { fileId } = createFolderId(input.request.folderId)

                    api.dispatch(addBindingsOptimisticUpdate(
                        { reportId: input.reportId, fileId: fileId },
                        setEntryBindingsOptimisticUpdateRecipe(request, entryId),
                        id)
                    );

                    const response = await api.queryFulfilled;
                    debounceInvalidateBindingStats(input.reportId);
                    api.dispatch(patchRemoteOpOperationId({ id: id, operationId: response.data.operationId }))
                    api.dispatch(patchRemoteOpVersion({ operationId: response.data.operationId, version: response.data.streamResponse.version }))
                    reportsStreamChanged.raise({
                        reportId: input.reportId,
                        version: response.data.streamResponse.version,
                        changes: response.data.streamResponse.changes
                    });
                }
                catch (error) {
                    api.dispatch(removeRemoteOp(id));
                }
            }
        }),
        addEntry: builder.mutation<AddEntryResponse, WithReportId<AddEntryRequest>>({
            query: request => ({
                url: `/stream/reports/${encodeURIComponent(request.reportId)}/entries`,
                method: 'POST',
                body: request
            }),
            onQueryStarted: async (input, api) => {
                let id: string | undefined;
                try {
                    const response = await api.queryFulfilled;
                    id = api.dispatch(addRemoteOperation(response.data.operationId, response.data.streamResponse!.version));
                    api.dispatch(addReportSchemaOptimisticUpdate(input.reportId, x => {
                        reportEntryAdapter.addOne(x.entries, {
                            id: response.data.entryId,
                            reportId: input.reportId,
                            title: input.definition.title,
                            labelId: '',
                            entryType: input.definition.entryType,
                            groupId: input.definition.groupId,
                            version: 0
                        });

                        if (input.definition.index !== undefined) {
                            x.report.entries.splice(input.definition.index, 0, response.data.entryId);
                        }
                    }, id));

                    reportsStreamChanged.raise({
                        reportId: input.reportId,
                        version: response.data.streamResponse.version,
                        changes: response.data.streamResponse.changes
                    });
                }
                catch (error) {
                    if (!!id) {
                        api.dispatch(removeRemoteOp(id));
                    }
                }
            }
        }),
        removeEntries: builder.mutation<BaseMutationResponse, RemoveEntriesInput>({
            query: request => ({
                url: `/stream/reports/${encodeURIComponent(request.reportId)}/entries/`,
                method: 'DELETE',
                body: request
            }),
            onQueryStarted: async (input, api) => {
                const id = api.dispatch(addRemoteOperation(input.operationId));
                api.dispatch(addReportSchemaOptimisticUpdate(input.reportId, x => {
                    reportEntryAdapter.removeMany(x.entries, input.entryIds);
                    x.report.entries = x.report.entries.filter(x => !input.entryIds.includes(x));
                }, id));

                try {
                    const response = await api.queryFulfilled;
                    api.dispatch(patchRemoteOpOperationId({ id: id, operationId: response.data.operationId }));
                    api.dispatch(patchRemoteOpVersion({ operationId: response.data.operationId, version: response.data.streamResponse.version }))
                    reportsStreamChanged.raise({
                        reportId: input.reportId,
                        version: response.data.streamResponse.version,
                        changes: response.data.streamResponse.changes
                    });
                }
                catch (error) {
                    api.dispatch(removeRemoteOp(id));
                }
            }
        }),
        labelEntry: builder.mutation<LabelReportEntryResponse, WithReportId<WithEntryId<LabelReportEntryRequest>>>({
            query: request => ({
                url: `/stream/reports/${encodeURIComponent(request.reportId)}/entries/${request.entryId}/label`,
                method: 'POST',
                body: request
            }),
            onQueryStarted: async (input, api) => {
                const operationId = input.operationId ?? createOperationId();
                const id = api.dispatch(addRemoteOperation(operationId));
                api.dispatch(addReportSchemaOptimisticUpdate(input.reportId, x => {
                    const entry = x.entries.entities[input.entryId];
                    if (!!entry) {
                        entry.title = input.label;
                    }
                }, id));

                try {
                    const response = await api.queryFulfilled;
                    api.dispatch(patchRemoteOpOperationId({ id: id, operationId: response.data.operationId }))
                    api.dispatch(patchRemoteOpVersion({ operationId: response.data.operationId, version: response.data.streamResponse.version }))
                    reportsStreamChanged.raise({
                        reportId: input.reportId,
                        version: response.data.streamResponse.version,
                        changes: response.data.streamResponse.changes
                    });
                }
                catch (error) {
                    api.dispatch(removeRemoteOp(id));
                }
            }
        }),
        addFlagToBinding: builder.mutation<BaseMutationResponse, FlagEntryBindingInput>({
            query: input => ({
                url: `/stream/reports/${encodeURIComponent(input.reportId)}/bindings/flag`,
                method: 'POST',
                body: input.request
            }),
            onQueryStarted: async (input, api) => {
                const operationId = input.request.operationId ?? createOperationId();
                const id = api.dispatch(addRemoteOperation(operationId));
                applyFlagBindingOptimisticUpdates(input, api, id, true);

                try {
                    const response = await api.queryFulfilled;
                    api.dispatch(reportsApiSlice.util.invalidateTags([{ type: 'Flagged' }]));
                    api.dispatch(patchRemoteOpOperationId({ id: id, operationId: response.data.operationId }))
                    api.dispatch(patchRemoteOpVersion({ operationId: response.data.operationId, version: response.data.streamResponse.version }))
                    reportsStreamChanged.raise({
                        reportId: input.reportId,
                        version: response.data.streamResponse.version,
                        changes: response.data.streamResponse.changes
                    });
                }
                catch (error) {
                    api.dispatch(removeRemoteOp(id));
                }
            }
        }),
        removeFlagFromBinding: builder.mutation<BaseMutationResponse, FlagEntryBindingInput>({
            query: input => ({
                url: `/stream/reports/${encodeURIComponent(input.reportId)}/bindings/flag`,
                method: 'DELETE',
                body: input.request
            }),
            onQueryStarted: async (input, api) => {
                const operationId = input.request.operationId ?? createOperationId();
                const id = api.dispatch(addRemoteOperation(operationId));
                applyFlagBindingOptimisticUpdates(input, api, id, false);

                try {
                    const response = await api.queryFulfilled;
                    api.dispatch(reportsApiSlice.util.invalidateTags([{ type: 'Flagged' }]));
                    api.dispatch(patchRemoteOpOperationId({ id: id, operationId: response.data.operationId }))
                    api.dispatch(patchRemoteOpVersion({ operationId: response.data.operationId, version: response.data.streamResponse.version }))
                    reportsStreamChanged.raise({
                        reportId: input.reportId,
                        version: response.data.streamResponse.version,
                        changes: response.data.streamResponse.changes
                    });
                }
                catch (error) {
                    api.dispatch(removeRemoteOp(id));
                }
            }
        }),
        removeAllBindings: builder.mutation<BaseMutationResponse, RemoveAllBindingsInput>({
            query: input => ({
                url: `/stream/reports/${encodeURIComponent(input.reportId)}/bindings`,
                method: 'DELETE',
                body: input.request
            }),
            onQueryStarted: async (input, api) => {
                const request = input.request;
                const operationId = request.operationId ?? createOperationId()
                const id = api.dispatch(addRemoteOperation(operationId));
                const { fileId } = createFolderId(request.folderId)
                api.dispatch(addBindingsOptimisticUpdate(
                    { reportId: input.reportId, fileId: fileId },
                    x => {
                        const entryBindings = x.entryBindings.entities[input.request.entryId];
                        if (!!entryBindings) {
                            entryBindings.bindings = [];
                        }
                    }, id))

                try {
                    const response = await api.queryFulfilled;
                    debounceInvalidateBindingStats(input.reportId);

                    api.dispatch(patchRemoteOpOperationId({ id: id, operationId: response.data.operationId }))
                    api.dispatch(patchRemoteOpVersion({ operationId: response.data.operationId, version: response.data.streamResponse.version }))
                    reportsStreamChanged.raise({
                        reportId: input.reportId,
                        version: response.data.streamResponse.version,
                        changes: response.data.streamResponse.changes
                    });
                }
                catch (error) {
                    api.dispatch(removeRemoteOp(id));
                }
            }
        }),
        addGroup: builder.mutation<AddGroupResponse, WithReportId<AddGroupRequest>>({
            query: request => ({
                url: `/stream/reports/${encodeURIComponent(request.reportId)}/groups/`,
                method: 'POST',
                body: request
            }),
            onQueryStarted: async (request, api) => {
                const operationId = request.operationId ?? createOperationId();
                const id = api.dispatch(addRemoteOperation(operationId));
                api.dispatch(addReportSchemaOptimisticUpdate(request.reportId, x => {
                    const newGroupId = nanoid();

                    const entryGroup: ReportEntryGroup = {
                        id: newGroupId,
                        label: request.label,
                        entries: request.entries ?? [],
                        reportId: request.reportId,
                        version: x.version
                    }
                    reportEntryGroupAdapter.addOne(x.groups, entryGroup);

                    if (request.orderedEntries !== undefined) {
                        x.report.entries = request.orderedEntries!;
                    }

                    request.entries?.forEach(entryId => x.entries.entities[entryId]!.groupId = newGroupId);
                }, id));

                try {
                    const response = await api.queryFulfilled;
                    api.dispatch(patchRemoteOpOperationId({ id: id, operationId: response.data.operationId }))
                    api.dispatch(patchRemoteOpVersion({ operationId: response.data.operationId, version: response.data.streamResponse.version }))
                    reportsStreamChanged.raise({
                        reportId: request.reportId,
                        version: response.data.streamResponse.version,
                        changes: response.data.streamResponse.changes
                    });
                } catch {
                    api.dispatch(removeRemoteOp(id));
                }
            }
        }),
        removeGroup: builder.mutation<BaseMutationResponse, WithReportId<BaseGroupRequest>>({
            query: request => ({
                url: `/stream/reports/${encodeURIComponent(request.reportId)}/groups/${encodeURIComponent(request.groupId)}`,
                method: 'DELETE'
            }),
            onQueryStarted: async (request, api) => {
                const operationId = request.operationId ?? createOperationId();
                const id = api.dispatch(addRemoteOperation(operationId));
                api.dispatch(addReportSchemaOptimisticUpdate(request.reportId, x => {
                    reportEntryGroupAdapter.removeOne(x.groups, request.groupId)
                }, id));

                try {
                    const response = await api.queryFulfilled;
                    api.dispatch(patchRemoteOpOperationId({ id: id, operationId: response.data.operationId }))
                    api.dispatch(patchRemoteOpVersion({ operationId: response.data.operationId, version: response.data.streamResponse.version }))
                    reportsStreamChanged.raise({
                        reportId: request.reportId,
                        version: response.data.streamResponse.version,
                        changes: response.data.streamResponse.changes
                    });
                } catch {
                    api.dispatch(removeRemoteOp(id));
                }
            }
        }),
        labelGroup: builder.mutation<BaseMutationResponse, WithReportId<LabelGroupRequest>>({
            query: request => ({
                url: `/stream/reports/${encodeURIComponent(request.reportId)}/groups/${encodeURIComponent(request.groupId)}/label`,
                method: 'PUT',
                body: request
            }),
            onQueryStarted: async (request, api) => {
                const operationId = request.operationId ?? createOperationId();
                const id = api.dispatch(addRemoteOperation(operationId));
                api.dispatch(addReportSchemaOptimisticUpdate(request.reportId, x => {
                    reportEntryGroupAdapter.updateOne(x.groups, {
                        id: request.groupId,
                        changes: { label: request.label }
                    })
                }, id));

                try {
                    const response = await api.queryFulfilled;
                    api.dispatch(patchRemoteOpOperationId({ id: id, operationId: response.data.operationId }))
                    api.dispatch(patchRemoteOpVersion({ operationId: response.data.operationId, version: response.data.streamResponse.version }))
                    reportsStreamChanged.raise({
                        reportId: request.reportId,
                        version: response.data.streamResponse.version,
                        changes: response.data.streamResponse.changes
                    });
                } catch {
                    api.dispatch(removeRemoteOp(id));
                }
            }
        }),
        addEntriesToGroup: builder.mutation<BaseMutationResponse, WithReportId<ModifyGroupEntriesRequest>>({
            query: request => ({
                url: `/stream/reports/${encodeURIComponent(request.reportId)}/groups/${encodeURIComponent(request.groupId)}/entries`,
                method: 'PUT',
                body: request
            }),
            onQueryStarted: async (request, api) => {
                const operationId = request.operationId ?? createOperationId();
                const id = api.dispatch(addRemoteOperation(operationId));
                api.dispatch(addReportSchemaOptimisticUpdate(request.reportId, x => {
                    const groupEntries = x.groups.entities[request.groupId]?.entries ?? [];
                    reportEntryGroupAdapter.updateOne(x.groups, {
                        id: request.groupId,
                        changes: { entries: [...groupEntries, ...request.entries] }
                    });

                    if (request.orderedEntries !== undefined) {
                        x.report.entries = request.orderedEntries!;
                    }

                    for (const entryId of request.entries) {
                        x.entries.entities[entryId]!.groupId = request.groupId;
                    }
                }, id));

                try {
                    const response = await api.queryFulfilled;
                    api.dispatch(patchRemoteOpOperationId({ id: id, operationId: response.data.operationId }))
                    api.dispatch(patchRemoteOpVersion({ operationId: response.data.operationId, version: response.data.streamResponse.version }))
                    reportsStreamChanged.raise({
                        reportId: request.reportId,
                        version: response.data.streamResponse.version,
                        changes: response.data.streamResponse.changes
                    });
                } catch {
                    api.dispatch(removeRemoteOp(id));
                }
            }
        }),
        removeEntriesFromGroup: builder.mutation<BaseMutationResponse, WithReportId<ModifyGroupEntriesRequest>>({
            query: request => ({
                url: `/stream/reports/${encodeURIComponent(request.reportId)}/groups/${encodeURIComponent(request.groupId)}/entries`,
                method: 'DELETE',
                body: request
            }),
            onQueryStarted: async (request, api) => {
                const operationId = request.operationId ?? createOperationId();
                const id = api.dispatch(addRemoteOperation(operationId));
                api.dispatch(addReportSchemaOptimisticUpdate(request.reportId, x => {
                    const modifiedEntries = x.groups.entities[request.groupId]?.entries.filter(e => !request.entries.includes(e)) ?? [];
                    reportEntryGroupAdapter.updateOne(x.groups, {
                        id: request.groupId,
                        changes: { entries: modifiedEntries }
                    })

                    if (request.orderedEntries !== undefined) {
                        x.report.entries = request.orderedEntries!;
                    }

                    for (const entryId of request.entries) {
                        x.entries.entities[entryId]!.groupId = undefined;
                    }
                }, id));

                try {
                    const response = await api.queryFulfilled;
                    api.dispatch(patchRemoteOpOperationId({ id: id, operationId: response.data.operationId }))
                    api.dispatch(patchRemoteOpVersion({ operationId: response.data.operationId, version: response.data.streamResponse.version }))
                    reportsStreamChanged.raise({
                        reportId: request.reportId,
                        version: response.data.streamResponse.version,
                        changes: response.data.streamResponse.changes
                    });
                } catch {
                    api.dispatch(removeRemoteOp(id));
                }
            }
        }),
        getGroupTabularPreview: builder.query<GroupTabularPreviewResponse, GetGroupTabularPreviewRequest>({
            query: request => `/stream/reports/${encodeURIComponent(request.reportId)}/groups/${encodeURIComponent(request.groupId)}/tabular-preview/${encodeURIComponent(request.documentId)}`,
            providesTags: (result, error, arg) => [{ type: 'GroupTabularOutput', id: arg.groupId }],
            keepUnusedDataFor: 300
        }),
        refreshGroupTabularPreviewSchema: builder.mutation<void, RefreshGroupTabularSchemaRequest>({
            query: request => ({
                url: `/stream/reports/${encodeURIComponent(request.reportId)}/groups/${encodeURIComponent(request.groupId)}/tabular-preview`,
                method: 'POST'
            }),
            invalidatesTags: (result, error, arg) => [{ type: 'GroupTabularOutput', id: arg.groupId }]
        }),
        downloadDetails: builder.mutation<string, string>({
            query: reportId => ({
                url: `/stream/reports/${encodeURIComponent(reportId)}/details`,
                method: 'GET',
                responseHandler: async (response) => {
                    return URL.createObjectURL(await response.blob());
                },
            })
        })
    })
});

const remoteOperationAdapter = createEntityAdapter<RemoteOperation>({
    sortComparer: (x, y) => x.createdAt - y.createdAt
});

const { selectAll: selectAllRemoteOps } = remoteOperationAdapter.getSelectors();

const targetEndpointVersionAdapter = createEntityAdapter<EndpointVersionPayload>({
    selectId: x => getCacheKey(x.endpoint, x.arg)
});

const endpointPatchAdapter = createEntityAdapter<EndpointPatchState>({
    selectId: x => x.cacheKey
});

const { selectAll: selectAllPatches } = endpointPatchAdapter.getSelectors();
const { selectById: selectTargetEndpointVersionById } = targetEndpointVersionAdapter.getSelectors();

interface EndpointPatchState {
    cacheKey: string;
    patchOps: PatchOperation[];
}

interface PatchRemoteOpVersionPayload {
    operationId: string;
    version: number;
}

interface PatchRemoteOpOperationIdPayload {
    id: string;
    operationId: string;
}

interface EndpointVersionPayload {
    endpoint: ReportsApiEndpoint;
    arg?: any;
    version: number;
    isTimestamp?: boolean;
}

interface ReportOperationRequests {
    activeAddEntryGroupRequest?: ReportEntryGroup;
}

export interface ReportOperationsSliceState {
    remoteOps: EntityState<RemoteOperation>;
    patchOps: EntityState<EndpointPatchState>;
    targetEndpointVersions: EntityState<EndpointVersionPayload>;
    requests: ReportOperationRequests;
}

export const reportOperationsSlice = createSlice({
    name: 'reportOperations',
    initialState: {
        remoteOps: remoteOperationAdapter.getInitialState(),
        patchOps: endpointPatchAdapter.getInitialState(),
        targetEndpointVersions: targetEndpointVersionAdapter.getInitialState(),
        requests: {
            activeAddEntryGroupRequest: undefined
        }
    } as ReportOperationsSliceState,
    reducers: {
        addRemoteOp(state, action: PayloadAction<AddRemoteOperationPayload>) {
            remoteOperationAdapter.addOne(state.remoteOps, {
                ...action.payload,
                createdAt: new Date().getTime()
            });
        },
        addPatchOp(state, action: PayloadAction<AddPatchOperationPayload>) {
            const remoteOp = state.remoteOps.entities[action.payload.id];
            if (!remoteOp) {
                return;
            }

            const key = getCacheKey(action.payload.endpoint, action.payload.arg);
            const patchState = state.patchOps.entities[key] || {
                cacheKey: key,
                patchOps: []
            };

            patchState.patchOps.push({
                ...action.payload,
                cacheKey: getCacheKey(action.payload.endpoint, action.payload.arg),
                createdAt: new Date().getTime()
            });

            endpointPatchAdapter.upsertOne(state.patchOps, patchState);
        },
        patchRemoteOpVersion(state, action: PayloadAction<PatchRemoteOpVersionPayload>) {
            const updates: Update<RemoteOperation>[] = []
            const toRemove: EntityId[] = [];
            for (const op of selectAllRemoteOps(state.remoteOps)) {
                if (op.operationId === action.payload.operationId) {
                    const hasPatchOps = selectAllPatches(state.patchOps).some(x => x.patchOps.some(y => y.id === op.id));
                    if (hasPatchOps) {
                        updates.push({
                            id: op.id,
                            changes: {
                                version: action.payload.version
                            }
                        })
                    }
                    else {
                        toRemove.push(op.id);
                    }
                }
            }

            if (updates.length > 0) {
                remoteOperationAdapter.updateMany(state.remoteOps, updates);
            }

            if (toRemove.length > 0) {
                remoteOperationAdapter.removeMany(state.remoteOps, toRemove);
            }
        },
        patchRemoteOpOperationId(state, action: PayloadAction<PatchRemoteOpOperationIdPayload>) {
            if (!!state.remoteOps.entities[action.payload.id]) {
                remoteOperationAdapter.updateOne(state.remoteOps, {
                    id: action.payload.id,
                    changes: {
                        operationId: action.payload.operationId
                    }
                })
            }
        },
        removeRemoteOp(state, action: PayloadAction<string>) {
            if (!!state.remoteOps.entities[action.payload]) {
                remoteOperationAdapter.removeOne(state.remoteOps, action.payload);

                let toRemove: EntityId[] | undefined = undefined;
                for (const id of state.patchOps.ids) {
                    const patchState = state.patchOps.entities[id]!;
                    if (!!patchState.patchOps.find(x => x.id === action.payload)) {
                        patchState.patchOps = patchState.patchOps.filter(x => x.id !== action.payload);
                        if (!patchState.patchOps.length) {
                            toRemove = toRemove || [];
                            toRemove.push(id);
                        }
                    }
                }

                if (!!toRemove?.length) {
                    endpointPatchAdapter.removeMany(state.patchOps, toRemove);
                }

            }
        },
        removeRemoteOpsByOperationId(state, action: PayloadAction<string>) {
            const remoteOp: RemoteOperation | undefined = remoteOperationAdapter.getSelectors().selectAll(state.remoteOps).find(x => x.operationId === action.payload);
            if (!!remoteOp) {
                remoteOperationAdapter.removeOne(state.remoteOps, remoteOp.id);

                let toRemove: EntityId[] | undefined = undefined;
                for (const id of state.patchOps.ids) {
                    const patchState = state.patchOps.entities[id]!;
                    if (!!patchState.patchOps.find(x => x.id === remoteOp.id)) {
                        patchState.patchOps = patchState.patchOps.filter(x => x.id !== remoteOp.id);

                        if (!patchState.patchOps.length) {
                            toRemove = toRemove || [];
                            toRemove.push(id);
                        }
                    }
                }

                if (!!toRemove?.length) {
                    endpointPatchAdapter.removeMany(state.patchOps, toRemove);
                }
            }
        },
        invalidateRemoteOpsForVersion(state, action: PayloadAction<EndpointVersionPayload>) {
            const { endpoint, arg, version, isTimestamp } = action.payload;

            const cacheKey = getCacheKey(endpoint, arg);
            const patchState = state.patchOps.entities[cacheKey];
            if (!patchState) {
                return;
            }

            const newPatchOps = patchState.patchOps.filter(x => {
                const remoteOp = state.remoteOps.entities[x.id];
                return !!remoteOp && ((!isTimestamp && (remoteOp.version === undefined || remoteOp.version > version)) || (!!isTimestamp && remoteOp.createdAt > version));
            });

            if (newPatchOps.length < patchState.patchOps.length) {
                patchState.patchOps = newPatchOps;
            }

            const toRemove = state.remoteOps.ids.filter(id => !selectAllPatches(state.patchOps).some(x => x.patchOps.some(y => y.id === id)));
            if (toRemove.length > 0) {
                remoteOperationAdapter.removeMany(state.remoteOps, toRemove);
            }

            const targetVersion = state.targetEndpointVersions.entities[cacheKey];
            if (!!targetVersion && targetVersion.version <= version) {
                targetEndpointVersionAdapter.removeOne(state.targetEndpointVersions, cacheKey);
            }

            if (!patchState.patchOps.length) {
                endpointPatchAdapter.removeOne(state.patchOps, cacheKey);
            }
        },
        addTargetEndpointVersion(state, action: PayloadAction<EndpointVersionPayload>) {
            const existing = selectTargetEndpointVersionById(state.targetEndpointVersions, targetEndpointVersionAdapter.selectId(action.payload))
            if (existing === undefined || existing.version < action.payload.version) {
                targetEndpointVersionAdapter.upsertOne(state.targetEndpointVersions, action.payload)
            }
        },
        setActiveAddEntryGroupRequest(state, action: PayloadAction<WithReportId<AddGroupRequest> | undefined>) {
            state.requests.activeAddEntryGroupRequest = !!action.payload ? {
                id: nanoid(),
                reportId: action.payload.reportId,
                label: action.payload.label,
                entries: action.payload.entries ?? [],
                // This version will be discarded when the add group request is sent
                version: 0
            } : undefined;
        }
    }
});

const getCacheKey = (endpointName: string | number | symbol, queryArgs: any) => `${endpointName.toString()}(${JSON.stringify(queryArgs, Object.keys(queryArgs || {}).sort())})`;

export const { addRemoteOp, patchRemoteOpOperationId, patchRemoteOpVersion, invalidateRemoteOpsForVersion, removeRemoteOpsByOperationId, removeRemoteOp, addPatchOp, addTargetEndpointVersion, setActiveAddEntryGroupRequest } = reportOperationsSlice.actions;

const addRemoteOperation = (operationId?: string, version?: number, operationMetadata?: OperationMetadata) => (dispatch: AppDispatch, getState: () => AppState) => {
    const id = nanoid(3);
    dispatch(addRemoteOp({
        id: id,
        operationId: operationId,
        version: version,
        operationMetadata: operationMetadata
    }));

    return id;
}

const addReportSchemaOptimisticUpdate = (reportId: string, recipe: Recipe<GetReportSchemaState>, localOperationId: string) => (dispatch: AppDispatch, getState: () => AppState) => {
    createOperation(
        "getReportSchema",
        selectPatchedReportSchemaState,
        recipe,
        getState,
        dispatch,
        localOperationId,
        reportId
    );
}

const addBindingsOptimisticUpdate = (input: GetBindingsInput, recipe: Recipe<GetBindingsState>, localOperationId: string) => (dispatch: AppDispatch, getState: () => AppState) => {
    createOperation(
        "getBindings",
        selectPatchedReportBindingsState,
        recipe,
        getState,
        dispatch,
        localOperationId,
        input
    );
}

const createOperation = (endpointName: ReportsApiEndpoint, patchedStateSelector: (state: AppState, arg?: any) => any, recipe: Recipe<any>, getState: () => AppState, dispatch: AppDispatch, localOperationId: string, arg?: any) => {
    const currentState = patchedStateSelector(getState(), arg);
    if (!currentState) {
        return;
    }

    const payload: AddPatchOperationPayload = {
        id: localOperationId,
        endpoint: endpointName,
        arg: arg,
        patches: []
    }

    produce(currentState, recipe, (patches) => {
        payload.patches = patches
    });

    if (payload.patches.length > 0) {
        dispatch(addPatchOp(payload));
    }
}

const createSelectPatchedEndpointState = <T>(endpointStateSelector: EndpointStateSelector, endpointName: ReportsApiEndpoint) => createSelector(
    [
        (state: AppState, arg?: any) => state.reportOperations.patchOps.entities[getCacheKey(endpointName, arg)],
        endpointStateSelector
    ],
    (ops, endpointState) => {
        if (!endpointState) {
            return undefined;
        }

        if (!ops?.patchOps?.length) {
            return endpointState as T;
        }

        let result = endpointState;
        for (const targetOp of ops.patchOps) {
            try {
                result = applyPatches(result, targetOp.patches);
            }
            catch (error) {
                // Apply patches at best effort
            }
        }

        return result as T;
    }
);

type EndpointStateSelector = (state: AppState, arg: any) => any;

export const selectGetReportSchemaState: EndpointStateSelector = (state: AppState, arg: string) => reportsApiSlice.endpoints.getReportSchema.select(arg)(state).data
export const selectGetBindingsState: EndpointStateSelector = (state: AppState, arg: GetBindingsInput) => reportsApiSlice.endpoints.getBindings.select(arg)(state).data;
export const selectGetReviewDocumentsState: EndpointStateSelector = (state: AppState, arg: string) => reportsApiSlice.endpoints.getReviewDocuments.select(arg)(state).data;
export const selectGetPublishedDocumentsState: EndpointStateSelector = (state: AppState, arg: string) => reportsApiSlice.endpoints.getPublishedDocuments.select(arg)(state).data;
export const selectGetEvaluationsPreviewState: EndpointStateSelector = (state: AppState, arg: GetEvaluationsInput) => reportsApiSlice.endpoints.getEvaluationsPreview.select(arg)(state).data;
export const selectGetReviewBindingStatsState: EndpointStateSelector = (state: AppState, arg: string) => reportsApiSlice.endpoints.getReviewBindingStats.select(arg)(state).data;

export const selectBasePatchedReportSchemaState = createSelectPatchedEndpointState<GetReportSchemaState>(selectGetReportSchemaState, "getReportSchema");
export const selectPatchedReportBindingsState = createSelectPatchedEndpointState<GetBindingsState>(selectGetBindingsState, "getBindings");

export const selectPatchedReportSchemaState = createSelector(
    (state: AppState, reportId: string) => reportId,
    (state: AppState, reportId: string) => selectBasePatchedReportSchemaState(state, reportId),
    (state: AppState) => state.reportOperations.requests.activeAddEntryGroupRequest,
    (reportId: string, schema: GetReportSchemaState | undefined, activeGroupRequest: ReportEntryGroup | undefined) => produce(schema, x => {
        if (!x) {
            return;
        }

        if (!!activeGroupRequest && activeGroupRequest.reportId === reportId) {
            for (const entryId of activeGroupRequest.entries) {
                reportEntryAdapter.updateOne(x.entries, {
                    id: entryId,
                    changes: {
                        groupId: activeGroupRequest.id
                    }
                });
            }

            reportEntryGroupAdapter.addOne(x.groups, activeGroupRequest);
        }
    })
);

export const {
    useGetReportSchemaQuery,
    useGetBindingsQuery,
    useGetPublishedDocumentsQuery,
    useGetEvaluationsPreviewQuery,
    useGetEvaluationQuery,
    useGetReviewDocumentsQuery,
    useGetReviewBindingStatsQuery,
    useGetPublishedAbstractExistsQuery,
    useLazyGetPublishedAbstractExistsQuery,
    useGetPublishedSingleDocumentExcelExistsQuery,
    useLazyGetPublishedSingleDocumentExcelExistsQuery,
    useGetPublishedExcelExistsQuery,
    useLazyGetPublishedExcelExistsQuery,
    useGetAbstractTemplateExistsQuery,
    useLazyGetAbstractTemplateExistsQuery,
    useGetAbstractPreviewExistsQuery,
    useLazyGetAbstractPreviewExistsQuery,
    useLazyGetFlaggedDocumentsQuery,
    useGetFlaggedDocumentsQuery,
    useGetMiddleOutSuggestionsQuery,
    useGetSuggestedReportStatusQuery,
    useRenameReportMutation,
    useBindEntryMutation,
    useAddEntryMutation,
    useBulkClearBindingsMutation,
    useConfirmDocumentsMutation,
    useLabelEntryMutation,
    useAddFlagToBindingMutation,
    useRemoveFlagFromBindingMutation,
    usePublishReportMutation,
    useUnpublishReportMutation,
    useRemoveEntriesMutation,
    useSetEntriesOrderMutation,
    useUpdateReportSettingsMutation,
    useReviewEvaluationsDeltaMutation,
    usePublishedEvaluationsDeltaMutation,
    useUploadAbstractTemplateMutation,
    useRemoveAllBindingsMutation,
    useAddGroupMutation,
    useLabelGroupMutation,
    useRemoveGroupMutation,
    useAddEntriesToGroupMutation,
    useRemoveEntriesFromGroupMutation,
    useGetGroupTabularPreviewQuery,
    useRefreshGroupTabularPreviewSchemaMutation,
    useDownloadDetailsMutation,
    useSetEntryBindingsMutation
} = reportsApiSlice;

export const {
    selectAll: selectAllOperations,
    selectById: selectOperationById,
    selectEntities: selectOperationEntities,
    selectIds: selectOperationIds,
    selectTotal: selectOperationsTotal
} = remoteOperationAdapter.getSelectors((state: AppState) => state.reportOperations.remoteOps);

export const {
    selectAll: selectAllBindings,
    selectById: selectBindingsById,
    selectEntities: selectBindingEntities,
    selectIds: selectBindingIds,
    selectTotal: selectBindingsTotal
} = reportEntryBindingsAdapter.getSelectors();

export const {
    selectAll: selectAllEntries,
    selectById: selectEntryById,
    selectEntities: selectEntryEntities,
    selectIds: selectEntryIds,
    selectTotal: selectEntriesTotal
} = reportEntryAdapter.getSelectors();

export const {
    selectAll: selectAllDocuments
} = reportDocumentsAdapter.getSelectors();

export const {
    selectAll: selectAllReportRemoteOperations,
    selectById: selectRemoteOperationById
} = remoteOperationAdapter.getSelectors();

export const selectFlaggedDocumentIds = (state: AppState, reportId: string) => reportsApiSlice.endpoints.getFlaggedDocuments.select({ reportId })(state).data;

// Optimistic update helpers
const bindEntryBindingsReplaceEntryIdOptimisticUpdateRecipe = (input: BindEntryRequest, oldEntryId: string, response: BindEntryResponse): Recipe<GetBindingsState> => x => {
    if (input.action === BindEntryAction.Add) {
        const key = oldEntryId;
        const entryBindings = x.entryBindings.entities[key];
        if (!entryBindings) {
            return;
        }

        reportEntryBindingsAdapter.removeOne(x.entryBindings, key);
        entryBindings.entryId = response.entryId;
        reportEntryBindingsAdapter.addOne(x.entryBindings, entryBindings);

        const binding = entryBindings.bindings.find(x => x.spanId === input.spanId);
        if (!binding) {
            return;
        }

        binding.entryId = response.entryId;
    }
}

const setEntryBindingsOptimisticUpdateRecipe = (input: SetEntryBindingsRequest, entryId: string): Recipe<GetBindingsState> => x => {
    let entryBindings: EntryBindings | undefined = x.entryBindings.entities[input.entryId];
    if (!!input.spanIds?.length) {
        if (!entryBindings) {
            entryBindings = {
                entryId: entryId,
                folderId: input.folderId,
                bindings: []
            }
        }

        const targetBindings = input.spanIds.map(spanId => ({
            id: nanoid(),
            entryId: entryId,
            folderId: input.folderId,
            isConfirmed: true,
            userConfirmed: true,
            instanceId: spanId,
            spanId: spanId,
            bindingProviderId: BindingProviderId.User,
            metadata: {}
        }));

        entryBindings.bindings = targetBindings;
        reportEntryBindingsAdapter.upsertOne(x.entryBindings, entryBindings);
    }
    else if (!!entryBindings) {
        entryBindings.bindings = [];
    }
}

const bindEntryBindingsOptimisticUpdateRecipe = (input: BindEntryRequest, entryId: string): Recipe<GetBindingsState> => x => {
    let entryBindings: EntryBindings | undefined = x.entryBindings.entities[entryId];
    if (input.action === BindEntryAction.Add) {
        if (!entryBindings) {
            entryBindings = {
                entryId: entryId,
                folderId: input.folderId,
                bindings: []
            }
        }

        if (!!input.previousSpanId && input.previousSpanId !== input.spanId) {
            entryBindings.bindings = entryBindings.bindings.filter(x => x.spanId !== input.previousSpanId);
        }

        if (!!input.spanId) {
            // Remove empty bindings
            entryBindings.bindings = entryBindings.bindings.filter(x => !!x.spanId);
        }
        else {
            // Remove non-empty bindings
            entryBindings.bindings = entryBindings.bindings.filter(x => !x.spanId);
        }

        const targetIndex = entryBindings.bindings.findIndex(x => x.spanId === input.spanId || !x.spanId);
        const targetBinding: ReportBinding = entryBindings.bindings[targetIndex] ?? {
            id: nanoid(),
            entryId: entryId,
            folderId: input.folderId,
            userConfirmed: true,
            bindingProviderId: BindingProviderId.User,
            spanId: input.spanId,
            metadata: {},
        };

        const isConfirm = !!targetBinding && targetBinding.spanId === input.spanId;
        targetBinding.spanId = input.spanId;

        if (!isConfirm) {
            targetBinding.bindingProviderId = BindingProviderId.User;
        }

        targetBinding.isConfirmed = true;
        targetBinding.userConfirmed = true;

        if (targetIndex !== -1) {
            entryBindings.bindings[targetIndex] = targetBinding;
        }
        else {
            entryBindings.bindings.push(targetBinding);
        }

        reportEntryBindingsAdapter.upsertOne(x.entryBindings, entryBindings);
    }
    else if (!!entryBindings) {
        const index = entryBindings.bindings.findIndex(x => x.spanId === input.spanId);
        if (index !== -1) {
            entryBindings.bindings.splice(index, 1);
        }
    }
}

const bindEntryReportSchemaReplaceEntryIdOptimisticUpdateRecipe = (input: BindEntryRequest, oldEntryId: string, response: BindEntryResponse): Recipe<GetReportSchemaState> => x => {
    if (!input.createEntryDefinition) {
        return
    }

    const index = x.report.entries.indexOf(oldEntryId);
    if (index === -1) {
        return;
    }

    x.report.entries.splice(index, 1, response.entryId)

    const entry = x.entries.entities[oldEntryId];
    if (!entry) {
        return;
    }

    reportEntryAdapter.removeOne(x.entries, entry.id);

    entry.id = response.entryId;
    entry.labelId = response.label?.id ?? '';
    reportEntryAdapter.addOne(x.entries, entry);
}

const bindEntryReportSchemaOptimisticUpdateRecipe = (input: BindEntryRequest, reportId: string, entryId: string): Recipe<GetReportSchemaState> => x => {
    if (!!input.createEntryDefinition) {
        const targetIndex = clamp(input.createEntryDefinition?.index !== undefined ? input.createEntryDefinition.index : x.report.entries.length, 0, x.report.entries.length);
        x.report.entries.splice(targetIndex, 0, entryId);
        reportEntryAdapter.addOne(x.entries, {
            id: entryId,
            reportId: reportId,
            title: input.createEntryDefinition!.title,
            labelId: '',
            entryType: input.createEntryDefinition!.entryType,
            groupId: input.createEntryDefinition!.groupId,
            version: x.version
        });
    }
}

const flagEntryBindingOptimisticUpdateRecipe = (input: FlagEntryBindingRequest, addFlag?: boolean): Recipe<GetBindingsState> => x => {
    const entryBindings: EntryBindings | undefined = x.entryBindings.entities[input.entryId] || {
        entryId: input.entryId,
        folderId: input.folderId,
        bindings: []
    }

    let binding = entryBindings.bindings.find(x => x.folderId === input.folderId && x.spanId === input.spanId);
    if (!binding) {
        binding = {
            id: nanoid(),
            entryId: input.entryId,
            folderId: input.folderId,
            isConfirmed: true,
            userConfirmed: true,
            bindingProviderId: BindingProviderId.User,
            metadata: {}
        };

        entryBindings.bindings.push(binding);
    }

    if (addFlag) {
        binding.metadata = binding.metadata || {};
        binding.metadata[dgDiligenceFlagKey] = dgDiligenceFlagValue;
        binding.isConfirmed = true;
        binding.userConfirmed = true;
    } else if (!!binding.metadata) {
        delete binding.metadata[dgDiligenceFlagKey];
    }

    reportEntryBindingsAdapter.upsertOne(x.entryBindings, entryBindings);
}

function applyFlagBindingOptimisticUpdates(input: FlagEntryBindingInput, api: any, operationId: string, addFlag: boolean) {
    // Add optimistic update for bindings
    const { fileId } = createFolderId(input.request.folderId)
    api.dispatch(addBindingsOptimisticUpdate(
        { reportId: input.reportId, fileId: fileId },
        flagEntryBindingOptimisticUpdateRecipe(input.request, addFlag),
        operationId)
    );
}

const handleVersionedEndpointResponse = (payload: EndpointVersionPayload, refresh?: () => void) => (dispatch: AppDispatch, getState: () => AppState) => {
    dispatch(invalidateRemoteOpsForVersion(payload));

    if (!!refresh) {
        const cacheKey = getCacheKey(payload.endpoint, payload.arg);
        const targetVersion = getState().reportOperations.targetEndpointVersions.entities[cacheKey]?.version;
        if (!!targetVersion && targetVersion > payload.version) {
            refresh();
        }
    }
}

interface ReportStreamChange {
    reportId: string;
    version: number;
    changes: ReportStreamChangeNotification
}

export const reportsStreamChanged: Event<ReportStreamChange> = new Event();

const processStreamResponse = (payload: ReportStreamChange) => (dispatch: AppDispatch, getState: () => AppState) => {
    const { reportId, version, changes } = payload;
    if (changes.userModifiedBindings) {
        debounceInvalidateBindingStats(reportId);
    }

    if (!!changes.modifiedBindingsDocumentIds?.length) {
        for (const fileId of changes.modifiedBindingsDocumentIds) {
            dispatch(reportsApiSlice.util.invalidateTags([{ type: 'Bindings', id: fileId }]));
        }
    }

    if (changes.reviewDocumentStateModified || changes.batchChanged || changes.publishChanged) {
        dispatch(addTargetEndpointVersion({ endpoint: 'getReviewDocuments', arg: reportId, version: version }));
        dispatch(reportsApiSlice.util.invalidateTags([{ type: "ReviewDocuments", id: reportId }]));
    }

    if (changes.publishedDocumentStateModified || changes.batchChanged || changes.publishChanged) {
        dispatch(addTargetEndpointVersion({ endpoint: 'getPublishedDocuments', arg: reportId, version: version }));
        dispatch(reportsApiSlice.util.invalidateTags([{ type: "PublishedDocuments", id: reportId }]));
    }

    if (changes.batchChanged || changes.publishChanged) {
        dispatch(reportsApiSlice.util.invalidateTags(["ReviewDocuments", "PublishedDocuments"]));
    }

    if (changes.schemaModified) {
        dispatch(addTargetEndpointVersion({ endpoint: 'getReportSchema', arg: reportId, version: version }));
        dispatch(reportsApiSlice.util.invalidateTags([{ type: 'Schema', id: reportId }]));
        debounceInvalidateBindingStats(reportId);
    }

    if (!!changes.entriesAdded) {
        dispatch(reportsApiSlice.util.invalidateTags([{ type: 'Bindings', id: reportId }, { type: 'ReviewEvaluationsPreview', id: reportId }]));
    }

    if (!!changes.outOfDateDocumentIds?.length) {
        for (const fileId of changes.outOfDateDocumentIds) {
            dispatch(reportsApiSlice.util.invalidateTags([{ type: 'Bindings', id: fileId }, { type: 'ReviewEvaluationsPreview', id: fileId }]));
        }
    }
}

const debounceInvalidateBindingStats = debounce(
    (reportId: string) => store.dispatch(reportsApiSlice.util.invalidateTags([{ type: 'ReviewBindingStats', id: reportId }])),
    1000,
    { leading: true, trailing: true, maxWait: 2000 });


reportsStreamChanged.addListener(change => store.dispatch(processStreamResponse(change)));

export const refreshSignalRDependencies = () => (dispatch: AppDispatch, getState: () => AppState) => {
    dispatch(reportsApiSlice.util.invalidateTags(["ReviewEvaluationsPreview", "Bindings", "Schema"]));
}

// Reports slice

export enum ReportFilter {
    Confirmed = 'Confirmed',
    Unconfirmed = 'Unconfirmed',
    Flagged = 'Flagged'
}

export enum ReportFilterAxis {
    Items = 'Items',
    Documents = 'Documents'
}

interface ReportsSliceState {
    reportId?: string,
    folderId?: string,
    clusterId?: string,
    fileId?: string,
    context?: ReportViewContext,
    viewMode?: ReportViewMode,
    reportLabels?: ReportLabels
    selectedLabel?: string;
    searchString: string;
    activeFilters: ReportFilter[]; // The report item filters that are actively toggled off
    queryInstanceId?: string;
    filterAxis: ReportFilterAxis;
}

export const reportsSlice = createSlice({
    name: 'reportsSlice',
    initialState: {
        searchString: "",
        activeFilters: [],
        filterAxis: ReportFilterAxis.Items
    } as ReportsSliceState,
    reducers: {
        setSearchString(state, action: PayloadAction<string>) {
            state.searchString = action.payload;
        },
        setFilters(state, action: PayloadAction<ReportFilter[]>) {
            state.activeFilters = action.payload;
        },
        addFilter(state, action: PayloadAction<ReportFilter>) {
            state.activeFilters = [...state.activeFilters, action.payload];
        },
        removeFilter(state, action: PayloadAction<ReportFilter>) {
            if (state.activeFilters.includes(action.payload)) {
                state.activeFilters = state.activeFilters.filter(x => x !== action.payload);
            }
        },
        setFilterAxis(state, action: PayloadAction<ReportFilterAxis>) {
            // Reset filter state when the axis changes
            if (action.payload !== state.filterAxis) {
                state.searchString = "";
                state.activeFilters = [];
                state.filterAxis = action.payload;
            }
        },
        setReportQuery(state, action: PayloadAction<ReportQueryParams>) {
            const folderId = !!action.payload.documentId ? createFolderId(action.payload.documentId) : undefined;
            if (!!folderId) {
                state.folderId = folderId.value;
                state.fileId = folderId.fileId;
            }
            else {
                delete state.folderId;
            }

            const clusterId = !folderId || folderId.clusterId === UnassignedDocSetId ? undefined : folderId.clusterId;
            if (!!clusterId) {
                state.clusterId = clusterId;
            }
            else {
                delete state.clusterId;
            }

            if (!!action.payload.context) {
                state.context = action.payload.context;
            }
            else {
                delete state.context;
            }

            state.viewMode = action.payload.mode

            if (!!action.payload.instanceId) {
                state.queryInstanceId = action.payload.instanceId;
            }
            else {
                delete state.queryInstanceId;
            }
        },
        setReportId(state, action: PayloadAction<string | undefined>) {
            if (!action.payload) {
                delete state.reportId;
            }
            else {
                state.reportId = action.payload
            }
        },
        setFolderId(state, action: PayloadAction<string | undefined>) {
            if (!action.payload) {
                delete state.reportId;
            }
            else {
                const folderId = createFolderId(action.payload);
                if (folderId.clusterId === UnassignedDocSetId) {
                    delete state.clusterId;
                }
                else {
                    state.clusterId = folderId.clusterId;
                }

                state.fileId = folderId.fileId;
                state.folderId = folderId.value;
            }
        },
        setContext(state, action: PayloadAction<ReportViewContext | undefined>) {
            if (!action.payload) {
                delete state.context;
            }
            else {
                state.context = action.payload
            }
        },
        setViewMode(state, action: PayloadAction<ReportViewMode>) {
            state.viewMode = action.payload
        },
        setBulkReportState(state, action: PayloadAction<Partial<ReportsSliceState>>) {
            state = {
                ...action.payload,
                ...state
            }
        },
        setReportLabels(state, action: PayloadAction<ReportLabels>) {
            if (!state.reportLabels) {
                state.reportLabels = action.payload;
            }
            else {
                if (action.payload.itemsName !== state.reportLabels?.itemsName) {
                    state.reportLabels.itemsName = action.payload.itemsName;
                }

                if (action.payload.titleName !== state.reportLabels.titleName) {
                    state.reportLabels.titleName = action.payload.titleName;
                }
            }
        },
        setSelectedLabel(state, action: PayloadAction<string | undefined>) {
            if (!action.payload) {
                delete state.selectedLabel;
            }
            else {
                state.selectedLabel = action.payload;
            }
        },
    }
});

export const {
    setSearchString,
    setFilters,
    addFilter,
    removeFilter,
    setReportId,
    setBulkReportState,
    setContext,
    setFolderId,
    setViewMode,
    setReportQuery,
    setReportLabels,
    setFilterAxis,
    setSelectedLabel } = reportsSlice.actions;

startAppListening({
    actionCreator: workPathsSlice.actions.setCurrentWorkPath,
    effect: (input, listenerApi) => {
        const reportId = input.payload?.resources[NamedResource.ReportId];
        listenerApi.dispatch(setReportId(reportId));
    }
});

export const handleLabelRepeatsChangedMessage = (message: LabelRepeatsChangedMessage) => (dispatch: AppDispatch, getState: () => AppState) => {
    const reportId = getState().reportsSlice.reportId;
    const docsetId = getState().reportsSlice.clusterId;

    if (!!reportId && docsetId === message.docsetId) {
        debounceInvalidateBindingStats(reportId);
        dispatch(reportsApiSlice.util.invalidateTags([
            { type: 'Bindings', id: reportId },
            { type: 'ReviewEvaluationsPreview', id: reportId }
        ]))
    }
}
